import React, { useEffect, useState } from 'react';
import { User, Mail, Phone, MapPin, CreditCard, Calendar, ShieldCheck, Briefcase, ArrowRightLeft, ArrowDownRight, ArrowUpRight, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Profile = ({ userData }: { userData?: any }) => {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState<any>(null);
  const [recentTxns, setRecentTxns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!userData || !userData.id) return;
      try {
        // Fetch Profile
        const profileRes = await fetch(`https://motilalalgo.com/api/profile/${userData.id}`);
        const profileJson = await profileRes.json();
        
        // Fetch Last 5 Transactions
        const txnRes = await fetch(`https://motilalalgo.com/api/profile/${userData.id}/transactions`);
        const txnJson = await txnRes.json();

        setProfileData(profileJson);
        setRecentTxns(txnJson);
      } catch (error) {
        console.error("Error fetching profile data", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [userData]);

  if (loading || !profileData) {
    return <div className="h-full flex items-center justify-center text-blue-500"><Loader className="animate-spin" size={40} /></div>;
  }

  // Handle Windows paths for image (Safety Checks Added)
  const photoUrl = profileData?.photo_doc ? `https://motilalalgo.com/${profileData.photo_doc.replace(/\\/g, '/')}` : '';
  const joinDate = profileData?.created_at ? new Date(profileData.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A';

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-10">
      
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">My Profile</h1>
        <p className="text-gray-400 mt-1">Your registered account and KYC information.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* LEFT COLUMN: Main Profile Card */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-[#131b2c] border border-gray-800 rounded-3xl p-6 shadow-xl text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-r from-blue-900/40 to-purple-900/40"></div>
            
            {/* Profile Photo */}
            <div className="relative inline-block mt-8 mb-4">
              {photoUrl ? (
                <img 
                  src={photoUrl} 
                  alt="Profile" 
                  className="w-32 h-32 rounded-full object-cover border-4 border-[#0b1221] shadow-xl relative z-10"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }} 
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-blue-600 flex items-center justify-center text-4xl font-bold text-white border-4 border-[#0b1221] shadow-xl relative z-10">
                  {profileData?.first_name?.charAt(0)}{profileData?.last_name?.charAt(0)}
                </div>
              )}
              <div className="absolute bottom-1 right-1 bg-green-500 w-6 h-6 rounded-full border-4 border-[#131b2c] z-20" title="Active Account"></div>
            </div>

            <h2 className="text-2xl font-bold text-white">{profileData?.first_name} {profileData?.last_name}</h2>
            <p className="text-blue-400 font-mono text-sm mt-1 mb-6">ID: {profileData?.login_id || 'N/A'}</p>

            <div className="flex justify-between items-center bg-[#0b1221] p-4 rounded-xl border border-gray-800 text-left">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">A/C Number</p>
                <p className="text-white font-mono font-bold">{profileData?.account_number || 'N/A'}</p>
              </div>
              <Briefcase className="text-gray-600" size={24} />
            </div>
            
            <p className="text-xs text-gray-500 mt-6">Member since {joinDate}</p>
          </div>
        </div>

        {/* RIGHT COLUMN: Details & Transactions */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Details Card */}
          <div className="bg-[#131b2c] border border-gray-800 rounded-3xl p-6 md:p-8 shadow-xl">
            <div className="flex items-center gap-3 mb-6 border-b border-gray-800 pb-4">
              <ShieldCheck className="text-green-500" size={24} />
              <h3 className="text-lg font-bold text-white">Verified Identity Details</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-8">
              {/* Read Only Fields */}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-widest mb-2 flex items-center gap-2"><Mail size={14}/> Email Address</p>
                <div className="bg-[#0b1221] border border-gray-800 px-4 py-3 rounded-xl text-white font-medium">{profileData?.email || 'N/A'}</div>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-widest mb-2 flex items-center gap-2"><Phone size={14}/> Mobile Number</p>
                <div className="bg-[#0b1221] border border-gray-800 px-4 py-3 rounded-xl text-white font-medium">{profileData?.mobile_number || 'N/A'}</div>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-widest mb-2 flex items-center gap-2"><Calendar size={14}/> Date of Birth</p>
                <div className="bg-[#0b1221] border border-gray-800 px-4 py-3 rounded-xl text-white font-medium">
                  {profileData?.dob ? new Date(profileData.dob).toLocaleDateString('en-GB') : 'N/A'}
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-widest mb-2 flex items-center gap-2"><CreditCard size={14}/> PAN Number</p>
                <div className="bg-[#0b1221] border border-gray-800 px-4 py-3 rounded-xl text-white font-mono uppercase">{profileData?.pan_no || 'N/A'}</div>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-widest mb-2 flex items-center gap-2"><User size={14}/> Aadhar Number</p>
                {/* YAHI FIX KIYA HAI: Optional chaining + ternary operator */}
                <div className="bg-[#0b1221] border border-gray-800 px-4 py-3 rounded-xl text-white font-mono">
                  {profileData?.aadhar_no ? profileData.aadhar_no.replace(/(\d{4})/g, '$1 ').trim() : 'N/A'}
                </div>
              </div>
              <div className="md:col-span-2">
                <p className="text-xs text-gray-500 uppercase tracking-widest mb-2 flex items-center gap-2"><MapPin size={14}/> Full Address</p>
                <div className="bg-[#0b1221] border border-gray-800 px-4 py-3 rounded-xl text-white leading-relaxed">{profileData?.full_address || profileData?.city || 'N/A'}</div>
              </div>
            </div>
          </div>

          {/* Recent Transactions Section */}
          <div className="bg-[#131b2c] border border-gray-800 rounded-3xl p-6 shadow-xl">
            <div className="flex justify-between items-center mb-6 border-b border-gray-800 pb-4">
              <div className="flex items-center gap-3">
                <ArrowRightLeft className="text-blue-500" size={24} />
                <h3 className="text-lg font-bold text-white">Recent Transactions</h3>
              </div>
              <button 
                onClick={() => navigate('/account/transactions')}
                className="text-sm font-bold text-blue-500 hover:text-blue-400 transition-colors"
              >
                View All
              </button>
            </div>

            <div className="space-y-3">
              {recentTxns.length > 0 ? (
                recentTxns.map((tx, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 bg-[#0b1221] border border-gray-800 rounded-xl hover:border-gray-700 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tx.type === 'DEPOSIT' ? 'bg-green-500/10 text-green-500' : tx.type === 'WITHDRAW' ? 'bg-red-500/10 text-red-500' : 'bg-blue-500/10 text-blue-500'}`}>
                        {tx.type === 'DEPOSIT' ? <ArrowDownRight size={20} /> : <ArrowUpRight size={20} />}
                      </div>
                      <div>
                        <p className="font-bold text-white text-sm">{tx.type === 'BUY' ? `Bought ${tx.asset}` : tx.type}</p>
                        <p className="text-[10px] text-gray-500">{new Date(tx.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold text-sm ${tx.type === 'DEPOSIT' ? 'text-green-500' : 'text-white'}`}>
                        {tx.type === 'DEPOSIT' ? '+' : '-'}₹{parseFloat(tx.amount).toFixed(2)}
                      </p>
                      <p className={`text-[10px] font-bold ${tx.status === 'SUCCESS' ? 'text-green-500' : 'text-orange-500'}`}>{tx.status}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 text-gray-500 text-sm">
                  <p>No recent transactions found.</p>
                </div>
              )}
            </div>
            
            <button 
              onClick={() => navigate('/account/transactions')}
              className="w-full mt-4 py-3 bg-[#1e222d] hover:bg-[#2a2e39] text-white font-bold rounded-xl border border-gray-700 transition-colors"
            >
              View All Transactions
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Profile;